import { ArrowLeft, BarChart3, Building2, Check, Plus, Download, Filter, LoaderCircle, RefreshCw, Save, Search, SlidersHorizontal, Sparkles, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DASHBOARD_DIMENSIONS,
  SNAPSHOT_CLOSED_CHOICES,
  activeFacetCount,
  emptyFacets,
  facetOptions,
  facetPayload,
  sourceOptions,
  writeFacets,
  VIEW_PREFIX,
  type DashboardDimension,
  type DashboardFacets
} from "@/shared/filters/snapshot-facets";
import { windowFromBounds } from "./upload-window";
import { polarityOptions } from "@/shared/types/polarity";
import { useNavigate } from "react-router-dom";
import { SAVED_PAGE_SIZE, savedPeriodRange, type SavedReportsQuery } from "@/features/reports/SavedReportsList";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import {
  apiClient,
  type BrandOut,
  type DashboardMetric,
  type SnapshotFacetFilters,
  type MentionScope,
  type SocialReportDraft,
  type SocialReportItem,
  type SocialReportItemOrder,
  type SocialReportRun,
  type SocialReportSummary,
  type UploadSummary
} from "@/shared/api/api-client";
import { toMention, type Mention } from "@/shared/types/mention";
import { AppLink } from "@/shared/components/AppLink";
import { CenaGauge } from "@/shared/components/CenaGauge";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { ApiError } from "@/shared/api/http";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatCurrencyCompact } from "@/shared/utils/format-currency";
import { ExportFormatDialog, type ExportChoice } from "@/features/exports/ExportFormatDialog";
import { ExportFeedback, useExportRequest } from "@/features/exports/use-export-request";
import {
  currentMonthRange,
  formatWindowFromCriteria,
  instantFromLocalInput,
  previousMonthRange,
  rangeDates,
  splitLocalInput
} from "@/shared/utils/date-range";
import { emptyRecorte, recorteToPayload, recorteToSearch, reportBrandNames, summarizeBrands, type ReportRecorte } from "@/features/reports/report-recorte";
import {
  criteriaChips,
  ReportCriteriaChips
} from "@/features/reports/ReportCriteriaChips";
import { useMentionFilters } from "@/features/mentions/use-mention-filters";
import { MentionFilterPanel } from "@/features/mentions/MentionFilterPanel";
import { emptyFilters, sanitizeMinAdValue } from "@/features/mentions/mention-filter-criteria";
import { uploadLabel } from "@/features/uploads/upload-shared";
import { IfCena, useShowsCena } from "@/shared/components/cena-visibility";
import { usePolarityScale } from "@/shared/components/polarity-choices";
import {
  MENTION_SEARCH_FIELDS,
  MENTION_SEARCH_FIELD_LABELS,
  type MentionSearchField
} from "@/shared/vocabulary/mention-search-fields";
import { MultiSelectFilter } from "@/shared/components/MultiSelectFilter";
import { PendingDraftsBanner } from "@/features/reports/PendingDraftsBanner";
import { SavedReportsList } from "@/features/reports/SavedReportsList";
import {
  SocialReportListHeader,
  SocialReportRow
} from "@/features/reports/social-report-items";

import { formatCena, formatInteger, formatShortDate } from "@/shared/utils/format";
import { Pager } from "@/shared/components/Pager";
import { ApiState } from "@/shared/components/ApiState";
import { DateField } from "@/shared/components/DateField";
import { Select } from "@/shared/components/Select";

/** Execução que ainda vai render resultado — enquanto está num destes,
 *  a tela polla e o botão fica travado. */
const RUNNING = new Set(["QUEUED", "STARTED"]);

const PAGE_SIZE = 25;

type Preset = { key: string; label: string; range: () => { from?: string; to?: string } };

/** Os controles do filtro de LEITURA que se populam pelo vocabulário da
 *  foto. Sentimento e fonte ficam de fora: são vocabulário FECHADO, e
 *  listá-los pelo que a foto tem esconderia a opção que ela não tem —
 *  "não há nenhuma negativa" é resposta, e some se a opção sumir.
 *
 *  As dicas de meio-escopo são as mesmas do dashboard, palavra por
 *  palavra, e importam MAIS aqui: num gráfico, "Tier 1" deixando a rede
 *  passar é uma fatia a mais; numa lista é um post de Instagram embaixo
 *  de um chip que diz "Tier 1". */
const VIEW_CONTROLS: Array<{
  key: DashboardDimension;
  label: string;
  hint?: string;
}> = [
  { key: "channels", label: "Canal" },
  { key: "approaches", label: "Abordagem" },
  { key: "brands", label: "Marca" },
  { key: "themeGroups", label: "Tema" },
  { key: "themes", label: "Subtema" },
  { key: "spokespersons", label: "Porta-voz" },
  {
    key: "tiers",
    label: "Tier do veículo",
    hint: "Só imprensa: recorta a imprensa e deixa as menções de redes passarem."
  },
  {
    key: "formats",
    label: "Formato",
    hint: "Só imprensa: recorta a imprensa e deixa as menções de redes passarem."
  },
  {
    key: "mediaTypes",
    label: "Tipo de mídia",
    hint: "Só imprensa: recorta a imprensa e deixa as menções de redes passarem. Amostra anterior a 25/10/2026 tem a imprensa toda em \"Não informado\"."
  },
  {
    key: "influencerTypes",
    label: "Porte do publicador",
    hint: "Só redes: recorta as redes e deixa as menções de imprensa passarem."
  },
  {
    key: "publisherGroups",
    label: "Grupo de publicador",
    hint: "Só redes: recorta as redes e deixa as menções de imprensa passarem."
  }
];

const PRESETS: Preset[] = [
  { key: "current", label: "Este mês", range: currentMonthRange },
  { key: "previous", label: "Mês passado", range: previousMonthRange },
  { key: "30d", label: "30 dias", range: () => rangeDates("30d") },
  { key: "90d", label: "90 dias", range: () => rangeDates("90d") }
];

export function SocialReportPage({
  token,
  companyId,
  openMention,
  initialRecorte = null,
  initialOpen = null,
  initialView = null,
  autoGenerate = false,
  readOnly = false
}: {
  token: string;
  companyId: string;
  openMention: (mention: Mention) => void;
  /** O recorte que veio da tela de Menções pela querystring. Lido UMA
   *  vez, no estado inicial — ver o docstring de `SocialReportRoute`. */
  initialRecorte?: ReportRecorte | null;
  /** Qual relatório reabrir, vindo de `?report=` / `?draft=`. */
  initialOpen?: { kind: "report" | "draft"; id: number } | null;
  /** O filtro de LEITURA que veio da URL, sob o prefixo `view.`. Não se
   *  confunde com `initialRecorte`, que é o recorte de GERAÇÃO. */
  initialView?: {
    facets: DashboardFacets;
    from: string;
    to: string;
    scope?: MentionScope | null;
  } | null;
  /** A pessoa veio de "Fechar amostra" na tela de Menções e a amostra
   *  deve ser fechada ao chegar, sem o segundo clique — SE o recorte já
   *  tem janela; sem janela, o formulário aparece como sempre. Lido UMA
   *  vez, no monte: a rota tira `gerar=1` da URL logo depois, e a janela
   *  da primeira visita pode chegar DEPOIS disso (ela vem do último dado,
   *  por requisição). */
  autoGenerate?: boolean;
  /** Só leitura — o Visualizador (2026-10-22): abre e lê os relatórios
   *  SALVOS da empresa, e não gera, recalcula, atualiza, exporta nem vê
   *  rascunho (o servidor recusa tudo isso pra ele). */
  readOnly?: boolean;
}) {
  const navigate = useNavigate();
  const showsCena = useShowsCena();
  const polarityScale = usePolarityScale();
  // O filtro de upload (`?upload=`) mora na URL desta rota, não na loja
  // compartilhada: vem do link que trouxe a pessoa até aqui. Semeado do
  // recorte da URL e devolvido ao hook como `uploadFilter`.
  const [uploadIds, setUploadIds] = useState<number[]>(
    initialRecorte?.uploadIds ?? []
  );
  // Os uploads pro seletor do painel e pro rótulo do chip, como a tela de
  // menções os carrega.
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const uploadLabelOf = useCallback(
    (id: number) => {
      const match = uploads.find(item => item.id === id);
      return match ? uploadLabel(match) : undefined;
    },
    [uploads]
  );
  useEffect(() => {
    let alive = true;
    apiClient
      .listUploads(token, { page: 1, size: 50 })
      .then(result => {
        if (alive) setUploads(result.items);
      })
      .catch(() => {
        // Sem a lista, o chip diz `#id` e o seletor do painel fica vazio.
      });
    return () => {
      alive = false;
    };
  }, [token]);
  const uploadOptions = useMemo(
    () =>
      uploads.filter(item => item.mention_count > 0 || uploadIds.includes(item.id)),
    [uploads, uploadIds]
  );

  // O recorte de GERAÇÃO é o MESMO filtro da tela de Menções (2026-10-17,
  // decisão da dona): janela, busca, escopo, marcas e as outras dimensões
  // moram na loja da casca e são lidos aqui pelo hook. Abrir um rascunho
  // ou relatório salvo NÃO mexe nele — só o "Editar recorte".
  const filterHook = useMentionFilters({
    token,
    uploadFilter: uploadIds,
    onChangeUploadFilter: setUploadIds,
    showsCena,
    polarityScale,
    uploadLabelOf
  });
  const { filters, updateFilters } = filterHook;
  // A janela é relógio de parede local (`YYYY-MM-DDTHH:mm`): tem HORA
  // (decisão de 2026-08-27). Vem dos campos de data e hora do filtro; o
  // instante só é gerado no envio.
  const windowStart = filters.dateFrom
    ? `${filters.dateFrom}T${filters.timeFrom || "00:00"}`
    : "";
  const windowEnd = filters.dateTo
    ? `${filters.dateTo}T${filters.timeTo || "23:59"}`
    : "";
  const writeWindow = (
    bound: "start" | "end",
    value: string
  ) => {
    const { day, time } = splitLocalInput(value);
    updateFilters(current =>
      bound === "start"
        ? { ...current, dateFrom: day, timeFrom: time }
        : { ...current, dateTo: day, timeTo: time }
    );
  };
  // Recorte só por upload, sem datas: a janela do relatório ficaria vazia
  // e a pessoa teria de adivinhar o período das menções. Então a janela
  // nasce do período delas. Só quando nenhuma ponta está preenchida (se
  // está, a pessoa escolheu — ou veio do filtro compartilhado) e só se
  // ela ainda não mexeu nos campos. As datas são hora de parede (texto,
  // sem fuso); falha na busca é conveniência perdida, não erro.
  const windowTouched = useRef(false);
  windowTouched.current = Boolean(filters.dateFrom || filters.dateTo);
  const uploadKey = uploadIds.join(",");
  useEffect(() => {
    const ids = uploadIds;
    if (ids.length === 0 || initialRecorte?.from || initialRecorte?.to) return;
    let alive = true;
    const base = { uploadIds: ids, size: 1 };
    Promise.all([
      apiClient.listMentions(token, { ...base, order: "published_asc" }, { withTotal: false }),
      apiClient.listMentions(token, { ...base, order: "published_desc" }, { withTotal: false })
    ])
      .then(([oldest, newest]) => {
        const first = oldest.items[0]?.published_at;
        const last = newest.items[0]?.published_at;
        if (!alive || windowTouched.current || !first || !last) return;
        const found = windowFromBounds(first, last);
        if (!found) return;
        const from = splitLocalInput(found.from);
        const to = splitLocalInput(found.to);
        updateFilters(current => ({
          ...current,
          dateFrom: from.day,
          timeFrom: from.time,
          dateTo: to.day,
          timeTo: to.time
        }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, uploadKey]);
  // O recorte que veio de Menções pela URL entra no filtro compartilhado
  // UMA vez, no monte, e SÓ se o filtro compartilhado está vazio: quem já
  // tem filtro guardado nesta empresa ficaria com ele trocado por um link
  // antigo. O filtro de upload não conta (ele mora na URL e já foi
  // semeado acima).
  const appliedInitial = useRef(false);
  useEffect(() => {
    if (appliedInitial.current) return;
    appliedInitial.current = true;
    if (!initialRecorte) return;
    const sharedEmpty =
      filterHook.activeFilterCount - uploadIds.length === 0 &&
      !filterHook.query.trim() &&
      filterHook.scope === "own";
    if (sharedEmpty) filterHook.applyRecorte(initialRecorte);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // As marcas só alimentam o filtro da lista de relatórios salvos (as da
  // empresa, não as do escopo do recorte).
  const [brands, setBrands] = useState<BrandOut[]>([]);

  const [run, setRun] = useState<SocialReportRun | null>(null);
  const [drafts, setDrafts] = useState<SocialReportDraft[]>([]);
  // O que está aberto: um rascunho OU um relatório salvo, nunca os
  // dois. Os dois tipos compartilham janela, marca e agregados, então o
  // cabeçalho da tela lê a união direto — sem normalizar e sem inventar
  // um rascunho de mentira pro relatório salvo.
  const [draft, setDraft] = useState<SocialReportDraft | null>(null);
  const [saved, setSaved] = useState<SocialReportSummary | null>(null);
  const [items, setItems] = useState<SocialReportItem[]>([]);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<SocialReportItemOrder>("icena_desc");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // A planilha vai pra fila (2026-10-15): o clique abre a escolha de
  // formato, o pedido volta na hora e o arquivo aparece em Downloads.
  const exporter = useExportRequest({
    token,
    onOpenDownloads: () => navigate(`/empresas/${companyId}/downloads`)
  });
  const [exportDialog, setExportDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Até quando o servidor pediu pra não perguntar nada (429). Ver
  // `usePolling`.
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [discarding, setDiscarding] = useState<SocialReportDraft | null>(null);
  // Diálogo de nome do salvar. `nameError` é separado de `error`: o erro
  // do nome pertence ao diálogo, não à página atrás dele.
  const [naming, setNaming] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [savedReports, setSavedReports] = useState<SocialReportSummary[]>([]);
  const [savedTotal, setSavedTotal] = useState(0);
  // Marca, período e página da lista de salvos (2026-10-13). Lidos por
  // ref no carregamento, pra `loadSavedReports` continuar estável e o
  // efeito de montagem não rodar de novo a cada filtro.
  const [savedQuery, setSavedQuery] = useState<SavedReportsQuery>({ brandId: null, period: "all", page: 1 });
  const savedQueryRef = useRef(savedQuery);
  savedQueryRef.current = savedQuery;


  // ---- filtro de LEITURA -------------------------------------------
  //
  // Recorta o que se VÊ da foto, e nada mais: a foto e a
  // `generation_criteria` são imutáveis, e nenhum controle daqui pode
  // acender o aviso de "recalcule" — ele é do recorte de GERAÇÃO,
  // e acendê-lo aqui mandaria a pessoa disparar um job pra aplicar um
  // filtro que já está aplicado.
  const [viewFacets, setViewFacets] = useState<DashboardFacets>(
    initialView?.facets ?? emptyFacets()
  );
  // A janela de LEITURA é por DIA, sobre a coluna congelada — mesma
  // semântica do dashboard, e diferente do formulário de geração ali em
  // cima, que é instante com hora. Dois calendários com significados
  // diferentes na mesma tela é justamente por isso que este mora no card
  // da lista e aquele no de geração.
  //
  // `<input type="date">` já devolve `YYYY-MM-DD`: nada a converter, e
  // é o que a regra da casa manda (os dígitos são o relógio).
  const [viewFrom, setViewFrom] = useState(initialView?.from ?? "");
  const [viewTo, setViewTo] = useState(initialView?.to ?? "");
  // De quem são as menções MOSTRADAS da foto. O padrão é a empresa,
  // mesmo num relatório misto (decisão da dona): o total e o ICena
  // médio que o cliente conhece não mudam só porque o concorrente
  // entrou na foto. `null` = "o padrão deste relatório", que é
  // `competitors` quando a foto só tem concorrente.
  const [viewScopeChoice, setViewScopeChoice] = useState<MentionScope | null>(
    initialView?.scope ?? null
  );
  const [viewOpen, setViewOpen] = useState(false);
  // O vocabulário de cada dimensão NESTA foto. Vem do dashboard do
  // mesmo relatório, SEM filtro — o dashboard filtrado devolve só as
  // fatias que sobraram, e usá-lo faria a lista de opções encolher a
  // cada escolha (marcar um canal esconderia os outros).
  //
  // Buscado sob demanda, na primeira abertura do painel: são ~9
  // agregações no servidor, e quem nunca filtra não deve pagá-las.
  const [viewCatalog, setViewCatalog] = useState<Record<
    string,
    DashboardMetric[] | null
  > | null>(null);
  const [viewCatalogBusy, setViewCatalogBusy] = useState(false);

  // `loadDetail` é `useCallback` com dependência `[token, order]`; pôr o
  // filtro ali o recriaria a cada escolha e redispararia o efeito de
  // monte. A ref dá o valor corrente sem entrar na identidade da função.
  const viewFiltersRef = useRef<() => SnapshotFacetFilters>(() => ({}));

  // O escopo da FOTO aberta, e o padrão de leitura que ele implica.
  const photoScope: MentionScope =
    (draft ?? saved)?.generation_criteria?.scope ?? "own";
  const defaultViewScope: MentionScope =
    photoScope === "competitors" ? "competitors" : "own";
  const viewScope: MentionScope =
    photoScope === "own" ? "own" : (viewScopeChoice ?? defaultViewScope);
  const viewFilters = () => ({
    ...facetPayload(viewFacets),
    ...(viewFrom ? { dateFrom: viewFrom } : {}),
    ...(viewTo ? { dateTo: viewTo } : {}),
    ...(viewScope !== "own" ? { mentionScope: viewScope } : {})
  });
  const viewActive =
    activeFacetCount(viewFacets) + (viewFrom ? 1 : 0) + (viewTo ? 1 : 0);
  viewFiltersRef.current = viewFilters;

  // Amostra salva OU rascunho (2026-11-03: "deixe o usuário exportar sem
  // ter que salvar amostra") — a mesma planilha, lida da foto de cada um.
  const pedirPlanilha = async (
    target: { kind: "report" | "draft"; id: number },
    choice: ExportChoice
  ) => {
    const ok = await exporter.request(
      {
        kind: target.kind === "report" ? "export_report" : "export_draft",
        subject_id: target.id,
        format: choice.format,
        ...(choice.columns ? { columns: choice.columns } : {}),
        header: choice.header,
        theme_layout: choice.theme_layout,
        highlight_only: choice.highlight_only
      },
      viewFilters()
    );
    if (ok) setExportDialog(false);
  };

  const limparView = () => {
    setViewFacets(emptyFacets());
    setViewFrom("");
    setViewTo("");
  };
  const mudarView = (
    key: DashboardDimension,
    mudanca: (facet: DashboardFacets[DashboardDimension]) => DashboardFacets[DashboardDimension]
  ) => setViewFacets(atual => ({ ...atual, [key]: mudanca(atual[key]) }));

  const toggleView = (key: DashboardDimension, value: string) =>
    mudarView(key, facet => {
      const values = new Set(facet.values);
      if (values.has(value)) values.delete(value);
      else values.add(value);
      return { ...facet, values };
    });

  const optionsForView = (key: DashboardDimension) => {
    const dimension = DASHBOARD_DIMENSIONS.find(item => item.key === key);
    const catalog = dimension?.catalog;
    return facetOptions(
      viewFacets[key].values,
      catalog ? (viewCatalog?.[catalog] ?? null) : null,
      SNAPSHOT_CLOSED_CHOICES[key]
    );
  };

  /** Os chips do que está recortando AGORA — removíveis, ao contrário
   *  dos da foto, que são registro do que foi congelado. */
  const viewChips = () =>
    DASHBOARD_DIMENSIONS.flatMap(({ key }) =>
      Array.from(viewFacets[key].values).map(value => ({
        key: `${key}:${value}`,
        label: `${viewFacets[key].exclude ? "exceto " : ""}${
          optionsForView(key).find(o => o.value === value)?.label ?? value
        }`,
        remove: () => toggleView(key, value)
      }))
    );

  /** O vocabulário desta foto, buscado na primeira abertura do painel.
   *
   *  SEM filtro de propósito: o dashboard filtrado devolve só as fatias
   *  que sobraram, e usá-lo faria a lista de opções encolher a cada
   *  escolha — marcar um canal esconderia os outros, e não haveria como
   *  acrescentar o segundo sem limpar o primeiro. */
  const carregarVocabulario = async () => {
    if (viewCatalog || viewCatalogBusy) return;
    const alvo = saved
      ? { reportId: saved.id }
      : draft
        ? { draftId: draft.id }
        : null;
    if (!alvo) return;
    setViewCatalogBusy(true);
    try {
      const painel = await apiClient.socialReportDashboard(token, alvo);
      setViewCatalog({
        channels: painel.channels,
        approaches: painel.approaches,
        formats: painel.formats,
        tiers: painel.tiers,
        influencerTypes: painel.influencer_types,
        publisherGroups: painel.publisher_groups,
        themeGroups: painel.theme_groups,
        themes: painel.themes,
        brands: painel.brands,
        spokespersons: painel.spokespersons
      });
    } catch {
      /* sem vocabulário os controles ainda listam o que já foi escolhido */
    } finally {
      setViewCatalogBusy(false);
    }
  };

  const message = (failure: unknown, fallback: string) =>
    failure instanceof Error ? failure.message : fallback;

  /**
   * As marcas do relatório, por extenso, pelos rótulos CONGELADOS.
   *
   * A coluna `brand_name` vem do join com `brand_id`, que é `null` num
   * relatório de duas marcas — sozinha, ela faria todo relatório
   * multi-marca parecer "empresa inteira". E os nomes saem dos
   * rótulos, não do catálogo vivo: renomear uma marca não reescreve o
   * que um relatório salvo diz.
   */
  // `reportBrandNames` e `summarizeBrands` moram em `report-recorte.ts`:
  // o dashboard fazia a MESMA resolução e cortava em DOIS nomes, pro
  // mesmo relatório. Dois é a régua das duas telas desde 2026-09-02.
  const brandNamesOf = reportBrandNames;
  const brandSummary = summarizeBrands;


  /** Quais dimensões do recorte só existem em METADE das menções.
   *
   *  Tier e formato são de imprensa; porte e grupo de publicador são de
   *  rede. Desde 2026-08-27 o filtro recorta só essa metade e deixa a
   *  outra passar inteira — o que é menos destrutivo que o
   *  comportamento anterior, e por isso mesmo precisa ser DITO: sem o
   *  aviso, a pessoa filtra por "Tier 1" e leva a rede toda junto sem
   *  entender de onde vieram as menções a mais. */
  const meioEscopoNoRecorte = (): string[] => {
    const avisos: string[] = [];
    const imprensa = ["tiers", "formats"] as const;
    const rede = ["influencerTypes", "publisherGroups"] as const;
    if (imprensa.some(key => filters.facets[key].values.size > 0)) {
      avisos.push("Tier e formato só existem em imprensa.");
    }
    if (rede.some(key => filters.facets[key].values.size > 0)) {
      avisos.push("Porte e grupo de publicador só existem em redes.");
    }
    if (sanitizeMinAdValue(filters.minAdValue) !== null) {
      avisos.push("Valoração só existe em imprensa — as redes entram inteiras.");
    }
    if (filters.titleCitation !== "all") {
      avisos.push("Citação no título só existe em imprensa — as redes entram inteiras.");
    }
    return avisos;
  };

  /** O que está no filtro compartilhado e NÃO vai pro relatório — o mesmo
   *  aviso que a tela de Menções dá ao gerar, mas aqui o filtro é o
   *  mesmo, então a lista fica à vista o tempo todo. A nota mínima fica
   *  de fora porque a rede só ganha nota DURANTE a geração; "editado
   *  por", "edição" e "só descartadas" são atributos da trilha ou do
   *  estado da menção, não da foto. */
  const naoViajam = (): string[] => {
    const fora: string[] = [];
    if (showsCena && filters.minCena.trim() !== "") fora.push("a nota mínima");
    if (filters.facets.editors.values.size > 0) fora.push("\"editado por\"");
    if (filters.edited !== "all") fora.push("\"edição\"");
    if (filters.onlyDiscarded) fora.push("\"só descartadas\"");
    return fora;
  };

  // A lista abre o drawer de menção que já existe — busca o detalhe
  // primeiro, igual `MentionsPage.openBrandMention`. Não vale uma tela
  // de detalhe própria: é a mesma publicação, vista de outro lugar.
  const openItem = useCallback(
    async (mentionId: number) => {
      try {
        openMention(toMention(await apiClient.mentionDetail(token, mentionId)));
      } catch (failure) {
        setError(message(failure, "Não foi possível abrir a publicação."));
      }
    },
    [token, openMention]
  );

  const loadDrafts = useCallback(async () => {
    try {
      setDrafts(await apiClient.listSocialReportDrafts(token));
    } catch {
      /* o banner é secundário: falhar aqui não pode derrubar a tela */
    }
  }, [token]);

  /** Os relatórios salvos da EMPRESA — todos, não só os de quem abriu a
   *  tela. Antes daqui eles só reapareciam no select do dashboard. */
  const loadSavedReports = useCallback(async () => {
    try {
      const q = savedQueryRef.current;
      const range = savedPeriodRange(q.period);
      const page = await apiClient.listSocialReports(token, {
        size: SAVED_PAGE_SIZE,
        page: q.page,
        brandId: q.brandId,
        dateFrom: range.from,
        dateTo: range.to
      });
      setSavedReports(page.items);
      setSavedTotal(page.total);
    } catch {
      /* a lista é secundária: falhar aqui não pode derrubar a tela */
    }
  }, [token]);

  const loadDetail = useCallback(
    async (draftId: number, nextPage = 1, nextOrder = order) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.socialReportDraft(token, draftId, {
          order: nextOrder,
          page: nextPage,
          size: PAGE_SIZE,
          filters: viewFiltersRef.current()
        });
        setDraft(result.draft);
        setSaved(null);
        setItems(result.items);
        setTotal(result.total);
        setPage(nextPage);
        // Abrir um rascunho NÃO mexe no filtro compartilhado (decisão da
        // dona, 2026-10-17): o recorte dele só vai pro filtro quando a
        // pessoa clica em "Editar recorte".
      } catch (failure) {
        setError(message(failure, "Não foi possível carregar o rascunho."));
      } finally {
        setLoading(false);
      }
    },
    [token, order]
  );

  // Monta: marcas pro filtro, rascunhos pendentes pro banner, e a última
  // execução — que é o que recupera uma geração em curso depois de um F5.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const list = await apiClient.brands(token);
        if (alive) setBrands(list);
      } catch {
        /* filtro de marca é opcional; sem ele a tela ainda funciona */
      }
      await Promise.all([loadDrafts(), loadSavedReports()]);
      try {
        const runs = await apiClient.listSocialReportRuns(token, { size: 1 });
        const last = runs.items[0];
        if (alive && last && RUNNING.has(last.status)) setRun(last);
      } catch {
        /* idem */
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, loadDrafts, loadSavedReports]);

  // Filtro ou página da lista de salvos mudou: recarrega só ela.
  const primeiraConsultaSalvos = useRef(true);
  useEffect(() => {
    if (primeiraConsultaSalvos.current) {
      primeiraConsultaSalvos.current = false;
      return;
    }
    void loadSavedReports();
  }, [savedQuery, loadSavedReports]);

  // Reabre o que a URL diz, uma vez. `initialOpen` vem de `?report=` /
  // `?draft=`, e é o que faz o F5 (e o link compartilhado) voltarem pro
  // mesmo relatório. Sem isto, a identidade na URL seria decoração — e
  // o filtro de leitura, que viaja junto, seria filtro sobre nada.
  //
  // Dispara só no MONTE: a partir daí quem manda no que está aberto é a
  // tela, e reler a URL desfaria um clique da pessoa.
  const abriuDaUrl = useRef(false);
  useEffect(() => {
    if (!initialOpen || abriuDaUrl.current) return;
    abriuDaUrl.current = true;
    if (initialOpen.kind === "report") void openSavedReport(initialOpen.id);
    else void loadDetail(initialOpen.id);
    // `openSavedReport` não é `useCallback` e mudaria a cada render — a
    // guarda acima é o que fixa "uma vez", não a lista de dependência.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpen, loadDetail]);

  // Qual relatório está aberto vai na URL, mesma convenção do dashboard
  // (`?report=` / `?draft=`). O filtro de upload é re-serializado junto:
  // reescrever a query sem ele o perderia no F5.
  useEffect(() => {
    // O filtro de GERAÇÃO mora na loja da casca (guardado no navegador);
    // da URL só fica o que é dela: o filtro de upload.
    const query = new URLSearchParams(
      uploadKey ? recorteToSearch({ ...emptyRecorte(), uploadIds }) : ""
    );
    if (saved) query.set("report", String(saved.id));
    else if (draft) query.set("draft", String(draft.id));
    // Sob `view.` pra não colidir com o recorte de GERAÇÃO, que usa
    // `from`, `to` e `minAdValue` sem prefixo. A colisão seria muda e
    // cara: um mínimo de valoração escolhido pra LER voltaria do F5
    // como recorte de geração e congelaria na próxima foto.
    writeFacets(query, viewFacets, VIEW_PREFIX);
    if (viewFrom) query.set(VIEW_PREFIX + "from", viewFrom);
    if (viewTo) query.set(VIEW_PREFIX + "to", viewTo);
    if (viewScopeChoice) query.set(VIEW_PREFIX + "scope", viewScopeChoice);
    navigate(
      `/empresas/${companyId}/relatorios/cena${query.size ? `?${query}` : ""}`,
      { replace: true, preventScrollReset: true }
    );
  }, [
    companyId,
    saved,
    draft,
    uploadKey,
    viewFacets,
    viewFrom,
    viewTo,
    viewScopeChoice,
    navigate
  ]);

  // Filtro novo recarrega a lista, SEMPRE da página 1. Sem o reset,
  // filtrar na página 7 devolveria vazio — e pior: quando o recorte cabe
  // numa página só, o `Pager` não renderiza nada, então não haveria nem
  // botão pra voltar. É a mesma regra, e o mesmo motivo, da tela de
  // menções.
  const primeiroRender = useRef(true);
  useEffect(() => {
    if (primeiroRender.current) {
      primeiroRender.current = false;
      return;
    }
    if (!saved && !draft) return;
    setPage(1);
    void reload(1);
    // `reload` fecha sobre estado que muda a cada render; a dependência
    // real desta reação é o filtro.
    // O escopo entra pelo valor RESOLVIDO, e não pela escolha: abrir um
    // relatório só de concorrentes muda o padrão de leitura sem ninguém
    // escolher nada, e a primeira carga (feita antes de se saber de quem
    // é a foto) pediu "a empresa" — que ali é lista vazia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFacets, viewScope, viewFrom, viewTo]);

  const refreshRun = useCallback(async () => {
    if (!run) return;
    try {
      const next = await apiClient.socialReportRun(token, run.id);
      setRun(next);
      if (!RUNNING.has(next.status)) {
        // Atualização de relatório SALVO: não há rascunho pra abrir — a
        // foto nova já está no próprio relatório, que é reaberto.
        if (next.target_report_id) {
          await openSavedRef.current?.(next.target_report_id);
          return;
        }
        await loadDrafts();
        if (next.report_draft_id) await loadDetail(next.report_draft_id);
      }
    } catch (failure) {
      // **O `run` FICA.** Descartá-lo aqui desligava o polling
      // (`enabled` deriva dele), sumia com o banner "Calculando…" e
      // destravava o botão de gerar — com o worker ainda calculando. O
      // próximo clique tomava o 409 da trava de execução ativa, com
      // cara de bug. E não era só 429: qualquer piscada de rede fazia
      // isso. Falha de ACOMPANHAMENTO vira aviso; a execução continua
      // sendo acompanhada, e o próximo tick bom limpa o aviso.
      if (
        failure instanceof ApiError &&
        failure.retryAfterSeconds !== null
      ) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setError(message(failure, "Não foi possível acompanhar a geração."));
    }
  }, [run, token, loadDrafts, loadDetail]);

  // Sem `processed_rows` equivalente, a assinatura quase não muda e o
  // backoff sobe rápido — o que é correto aqui: o cálculo grava de uma
  // vez só no fim, então perguntar a cada 2,5s não traria dado novo.
  usePolling({
    enabled: run !== null && RUNNING.has(run.status),
    onTick: () => void refreshRun(),
    signature: run ? `${run.id}:${run.status}` : undefined,
    pausedUntil
  });

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const recorte = filterHook.reportRecorte();
      const started = await apiClient.requestSocialReport(token, {
        windowStart: instantFromLocalInput(recorte.from, "start") ?? "",
        windowEnd: instantFromLocalInput(recorte.to, "end") ?? "",
        recorte: recorteToPayload(recorte)
      });
      setRun(started);
      setDraft(null);
      setSaved(null);
      setItems([]);
      setTotal(0);
    } catch (failure) {
      setError(message(failure, "Não foi possível gerar a amostra."));
    } finally {
      setBusy(false);
    }
  };

  // "Fechar amostra" em um clique: a geração dispara sozinha assim que a
  // janela existe (pode chegar depois do monte, da janela padrão da
  // primeira visita), uma vez só. Guardado em ref no monte porque a rota
  // tira `gerar=1` da URL em seguida e a prop volta a `false`.
  const wantsAutoGenerate = useRef(autoGenerate && !readOnly);
  const generationInFlight = run !== null && RUNNING.has(run.status);
  useEffect(() => {
    if (!wantsAutoGenerate.current) return;
    if (!windowStart || !windowEnd || busy || generationInFlight) return;
    wantsAutoGenerate.current = false;
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, windowEnd, busy, generationInFlight]);

  /**
   * Refaz a foto do relatório SALVO com o recorte original sobre a base
   * de hoje (2026-10-05, decisão da dona). Ato deliberado: nada avisa
   * que a base mudou, e nada atualiza sozinho. Acompanha como qualquer
   * geração; ao terminar, o relatório é reaberto com os números novos.
   */
  const refreshSavedData = async () => {
    if (!saved) return;
    setBusy(true);
    setError(null);
    try {
      setRun(await apiClient.refreshSocialReport(token, saved.id));
    } catch (failure) {
      setError(message(failure, "Não foi possível atualizar os dados da amostra."));
    } finally {
      setBusy(false);
    }
  };

  const recalculate = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const recorte = filterHook.reportRecorte();
      const started = await apiClient.recalculateSocialReportDraft(
        token,
        draft.id,
        {
          windowStart: instantFromLocalInput(recorte.from, "start"),
          windowEnd: instantFromLocalInput(recorte.to, "end"),
          brandIds: recorte.facets.brands.values.map(Number),
          excludeBrands: recorte.facets.brands.exclude,
          scope: recorte.scope,
          // A marca vem do filtro: o que ele mostra É o recorte de marca.
          // As outras dimensões o servidor herda do rascunho — o
          // recálculo só aceita janela, marca e escopo; mudar outra
          // dimensão vira uma geração nova (ver `canRecalculate`).
          brandsGiven: true
        }
      );
      setRun(started);
    } catch (failure) {
      setError(message(failure, "Não foi possível recalcular o rascunho."));
    } finally {
      setBusy(false);
    }
  };

  const promote = async (name: string) => {
    if (!draft) return;
    setBusy(true);
    setNameError(null);
    try {
      const { report_id } = await apiClient.promoteSocialReportDraft(
        token,
        draft.id,
        { name }
      );
      // Com o MESMO filtro de leitura que estava na tela: o salvo é a
      // mesma foto do rascunho, e sem ele a lista e os KPIs voltavam
      // pra foto inteira com os chips e o aviso de "recorte ativo"
      // ainda dizendo que o filtro estava aplicado (num rascunho só de
      // concorrentes, a lista ficava vazia).
      const result = await apiClient.socialReport(token, report_id, {
        order,
        size: PAGE_SIZE,
        filters: viewFilters()
      });
      setNaming(false);
      setError(null);
      // Troca de identidade: o rascunho deixou de existir (a promoção o
      // apaga), e o que está aberto agora é o relatório salvo.
      setDraft(null);
      setSaved(result.report);
      setItems(result.items);
      setTotal(result.total);
      setPage(1);
      await Promise.all([loadDrafts(), loadSavedReports()]);
    } catch (failure) {
      // No próprio diálogo: o nome recusado é corrigido ali, sem a
      // pessoa perder o que digitou.
      setNameError(message(failure, "Não foi possível salvar a amostra."));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Recarrega página/ordenação do que está aberto — rascunho ou salvo.
   *
   * Existe porque a promoção APAGA o rascunho: antes daqui, paginar
   * depois de salvar pedia um `draft_id` que não existia mais, e a tela
   * respondia "não foi possível carregar o rascunho" num relatório que
   * tinha acabado de ser salvo com sucesso.
   */
  const reload = async (
    nextPage: number,
    nextOrder: SocialReportItemOrder = order
  ) => {
    setLoading(true);
    setError(null);
    try {
      const filters = viewFilters();
      const result = saved
        ? await apiClient.socialReport(token, saved.id, {
            order: nextOrder,
            page: nextPage,
            size: PAGE_SIZE,
            filters
          })
        : draft
          ? await apiClient.socialReportDraft(token, draft.id, {
              order: nextOrder,
              page: nextPage,
              size: PAGE_SIZE,
              filters
            })
          : null;
      if (!result) return;
      setItems(result.items);
      setTotal(result.total);
      setPage(nextPage);
      setOrder(nextOrder);
      // O cabeçalho vem na MESMA resposta e traz os agregados já
      // filtrados: os KPIs seguem o filtro de leitura (decisão da dona,
      // 2026-09-08). Sem esta linha eles ficariam nos números da foto
      // inteira enquanto a lista mostra o recorte — a discordância entre
      // dois números da mesma tela que a entrega existe pra evitar.
      if ("report" in result) setSaved(result.report);
      else setDraft(result.draft);
    } catch (failure) {
      setError(message(failure, "Não foi possível carregar as publicações."));
    } finally {
      setLoading(false);
    }
  };

  /** Abre um relatório já salvo, pela lista. Mesmo caminho do
   *  pós-promoção — é o que a união de cabeçalho compra. */
  const openSavedReport = async (reportId: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.socialReport(token, reportId, {
        order,
        size: PAGE_SIZE,
        filters: viewFilters()
      });
      setDraft(null);
      setSaved(result.report);
      setItems(result.items);
      setTotal(result.total);
      setPage(1);
      // Também não mexe no filtro compartilhado — ver `loadDetail`.
    } catch (failure) {
      setError(message(failure, "Não foi possível abrir a amostra."));
    } finally {
      setLoading(false);
    }
  };

  // O acompanhamento da execução (`refreshRun`, acima) reabre o
  // relatório quando uma atualização termina, e é declarado antes desta
  // função — a referência é o elo.
  const openSavedRef = useRef(openSavedReport);
  openSavedRef.current = openSavedReport;

  const confirmDiscard = async () => {
    if (!discarding) return;
    setBusy(true);
    try {
      await apiClient.discardSocialReportDraft(token, discarding.id);
      if (draft?.id === discarding.id) {
        setDraft(null);
        setItems([]);
        setTotal(0);
      }
      setDiscarding(null);
      await loadDrafts();
    } catch (failure) {
      setError(message(failure, "Não foi possível descartar o rascunho."));
    } finally {
      setBusy(false);
    }
  };

  const applyPreset = (preset: Preset) => {
    const { from, to } = preset.range();
    // Preset é dia inteiro: as pontas viram 00:00 e 23:59, que é o que
    // "este mês" sempre quis dizer.
    updateFilters(current => ({
      ...current,
      ...(from ? { dateFrom: from, timeFrom: "00:00" } : {}),
      ...(to ? { dateTo: to, timeTo: "23:59" } : {})
    }));
  };

  const running = run !== null && RUNNING.has(run.status);
  // O cabeçalho é a união: o que estiver aberto responde por janela,
  // marca e agregados com os MESMOS nomes de campo nos dois tipos.
  const header = saved ?? draft;
  const aggregates = header?.aggregates ?? null;

  // ── Organização da tela (2026-10-13): dois momentos ──
  // Sem relatório aberto, a página é a "biblioteca" (rascunhos, salvos e
  // "Novo relatório"); com um aberto, é a leitura dele, com as ações num
  // cabeçalho fixo e o recorte de geração recolhido em "Editar recorte".
  // Estado só de tela: nada aqui muda o que é gerado ou gravado.
  const [formOpen, setFormOpen] = useState(false);
  const headerKey = saved ? `report-${saved.id}` : draft ? `draft-${draft.id}` : null;
  useEffect(() => {
    if (headerKey) setFormOpen(false);
  }, [headerKey]);
  // "Editar recorte" carrega a criteria congelada do relatório no filtro
  // compartilhado e guarda a chave do recorte logo depois — o aviso de
  // "recorte alterado" é a diferença entre as duas, DERIVADA (não há mais
  // um estado `stale` que cada controle precisava lembrar de acender).
  // A chave só é fotografada num efeito, depois de o filtro ter o valor
  // novo, e enquanto o catálogo ainda não chegou ela é refeita: os ids de
  // um nome de catálogo podem se expandir quando ele chega (homônimos), e
  // isso não é mudança da pessoa.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [baselining, setBaselining] = useState(false);
  const currentRecorte = filterHook.reportRecorte();
  const recorteKey = JSON.stringify(currentRecorte);
  useEffect(() => {
    if (!baselining) return;
    setLoadedKey(recorteKey);
    if (!filterHook.waitingForCatalog) setBaselining(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselining, recorteKey, filterHook.waitingForCatalog]);
  const stale =
    header !== null && formOpen && !baselining && loadedKey !== null && recorteKey !== loadedKey;
  // O recálculo de rascunho só aceita janela, marca e escopo; as outras
  // dimensões o servidor herda do rascunho. Se a pessoa mexeu em outra
  // (ou na busca, no filtro de upload...), recalcular a ignoraria em
  // silêncio — então o botão passa a GERAR um relatório novo com o
  // recorte inteiro, que é o que ela pediu.
  const inheritedKey = (recorte: ReportRecorte) =>
    JSON.stringify({
      ...recorte,
      from: "",
      to: "",
      scope: "own",
      facets: { ...recorte.facets, brands: { values: [], exclude: false } }
    });
  const canRecalculate =
    draft !== null &&
    saved === null &&
    loadedKey !== null &&
    inheritedKey(JSON.parse(loadedKey) as ReportRecorte) === inheritedKey(currentRecorte);
  const savedFiltered = savedQuery.brandId !== null || savedQuery.period !== "all";
  const libraryEmpty = drafts.length === 0 && savedTotal === 0 && !savedFiltered;
  const showForm = !readOnly && (header ? formOpen : formOpen || libraryEmpty || running);
  // Sem relatório aberto, ou com o formulário fechado, não há painel de
  // geração: ele não pode ficar órfão aberto.
  useEffect(() => {
    if (!showForm && filterHook.filtersOpen) filterHook.closePanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, filterHook.filtersOpen]);
  /** Volta pra biblioteca: o mesmo caminho do descarte, sem apagar nada. */
  const closeReport = () => {
    setDraft(null);
    setSaved(null);
    setItems([]);
    setTotal(0);
    setViewOpen(false);
  };
  const reportTitle = header
    ? saved?.name ??
      `${brandSummary(brandNamesOf(header), "Todas as marcas")} — ${formatWindowFromCriteria(
        header.generation_criteria,
        header.window_start,
        header.window_end
      )}`
    : "";

  return (
    <div className="report-page">
      {!header ? (
        <section className="report-library-head">
          {/* Sem <h2>: a topbar já diz "Amostras CENA de redes". */}
          <div>
            <p>
              Diferente da imprensa, o CENA de redes compara cada publicação
              com as outras do período — por isso ele é calculado aqui, e
              não no upload.
            </p>
          </div>
          {!libraryEmpty && !running && !readOnly && (
            <button
              type="button"
              className="primary-button"
              aria-expanded={formOpen}
              onClick={() => setFormOpen(current => !current)}
            >
              {formOpen ? <X size={15}/> : <Plus size={15}/>}
              {formOpen ? "Fechar" : "Nova amostra"}
            </button>
          )}
        </section>
      ) : (
        <section className="report-bar" aria-label="Amostra aberta">
          <div className="report-bar-main">
            <button type="button" className="report-bar-back" onClick={closeReport}>
              <ArrowLeft size={15}/> Amostras
            </button>
            <div className="report-bar-title">
              <h2>{reportTitle}</h2>
              <span className={`report-bar-status${saved ? " is-saved" : ""}`}>
                {saved ? (
                  <>
                    <Check size={12}/> Salva
                    {saved.saved_at && ` em ${formatShortDate(new Date(saved.saved_at))}`}
                    {saved.data_refreshed_at &&
                      ` · dados de ${formatShortDate(new Date(saved.data_refreshed_at))}`}
                  </>
                ) : (
                  <>Rascunho não salvo</>
                )}
              </span>
            </div>
          </div>
          <div className="report-bar-actions">
            {!readOnly && <button
              type="button"
              className={`report-bar-button${formOpen ? " is-active" : ""}`}
              aria-expanded={formOpen}
              onClick={() => {
                // Abrir é que carrega o recorte da foto no filtro
                // compartilhado; fechar não mexe em nada.
                if (!formOpen && header) {
                  filterHook.applyGenerationCriteria(
                    header.generation_criteria ?? {
                      schema_version: 1,
                      brand_id: header.brand_id,
                      window_start: header.window_start,
                      window_end: header.window_end
                    },
                    header.generation_criteria_labels,
                    header.brand_id
                  );
                  setBaselining(true);
                }
                setFormOpen(current => !current);
              }}
            >
              <SlidersHorizontal size={15}/> Editar recorte
            </button>}
            <AppLink
              className="report-bar-button"
              to={
                saved
                  ? `/empresas/${companyId}/dashboard?report=${saved.id}`
                  : `/empresas/${companyId}/dashboard?draft=${draft?.id}`
              }
            >
              <BarChart3 size={15}/> Ver no dashboard
            </AppLink>
            {/* Só o relatório SALVO atualiza. Exportar vale pros dois
                (2026-11-03): o rascunho também é foto, e a planilha sai
                dela tal como está agora. */}
            {saved && !readOnly && (
              <button
                type="button"
                className="report-bar-button"
                disabled={busy || (run !== null && RUNNING.has(run.status))}
                title="Refaz os números com o mesmo recorte sobre a base de hoje. Substitui a foto desta amostra."
                onClick={() => void refreshSavedData()}
              >
                {run !== null && RUNNING.has(run.status) && run.target_report_id === saved.id
                  ? <LoaderCircle size={15} className="spin"/>
                  : <RefreshCw size={15}/>}
                Atualizar dados
              </button>
            )}
            {(saved || draft) && !readOnly && (
              <button
                type="button"
                className={`report-bar-button${saved ? " is-primary" : ""}`}
                disabled={exporter.busy || (!saved && running)}
                title={saved ? undefined : "Exporta a planilha do rascunho como está agora, sem salvar a amostra."}
                onClick={() => {
                  exporter.clearError();
                  setExportDialog(true);
                }}
              >
                {exporter.busy ? <LoaderCircle size={15} className="spin"/> : <Download size={15}/>}
                {exporter.busy ? "Gerando…" : "Exportar planilha"}
              </button>
            )}
            {!saved && (
              <button
                type="button"
                className="report-bar-button"
                onClick={() => draft && setDiscarding(draft)}
                disabled={busy}
              >
                <Trash2 size={15}/> Descartar
              </button>
            )}
            {!saved && (
              <button
                type="button"
                className="report-bar-button is-primary"
                onClick={() => {
                  setNameError(null);
                  setNaming(true);
                }}
                disabled={busy || running}
              >
                <Save size={15}/> Salvar amostra
              </button>
            )}
          </div>
          {/* O recorte da foto, como registro (não são botões). */}
          <ReportCriteriaChips
            items={criteriaChips(
              header.generation_criteria,
              header.generation_criteria_labels
            )}
            prefix="Recorte da foto"
          />
        </section>
      )}


      {showForm && (
      <section className="card filter-panel report-filters">
        <div className="report-form-head">
          <h3 className="report-form-title">
            {header ? "Editar recorte" : "Nova amostra"}
            <small>
              {header
                ? "Mudar o recorte gera a foto de novo."
                : "É o mesmo filtro da tela de Menções: o que você marcou lá já está aqui."}
            </small>
          </h3>
          <div className="filter-chip-row">
            {PRESETS.map(preset => (
              <button
                key={preset.key}
                type="button"
                className="filter-chip"
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        <div className="report-form-row">
          <div className="search-field">
            <Search size={17} />
            <input
              name="report-search"
              autoComplete="off"
              aria-label="Buscar nas menções da amostra"
              value={filterHook.query}
              onChange={event => filterHook.setQuery(event.target.value)}
              placeholder="Buscar por título ou conteúdo…"
              title="Aceita AND, OR, NOT e parênteses. Aspas = palavra ou frase inteira. Sem operador entre palavras vale AND."
            />
            <Select
              aria-label="Buscar em"
              value={filterHook.searchIn}
              onChange={event =>
                filterHook.setSearchIn(event.target.value as MentionSearchField)
              }
            >
              {MENTION_SEARCH_FIELDS.map(field => (
                <option key={field} value={field}>
                  {MENTION_SEARCH_FIELD_LABELS[field]}
                </option>
              ))}
            </Select>
          </div>
          <div className="report-filter-fields">
            <label>
              Início
              <DateField
                ariaLabel="Data de início da amostra"
                value={windowStart}
                withTime
                onValue={next => writeWindow("start", next)}
              />
            </label>
            <label>
              Fim
              <DateField
                ariaLabel="Data de término da amostra"
                value={windowEnd}
                withTime
                defaultTime="23:59"
                align="right"
                onValue={next => writeWindow("end", next)}
              />
            </label>
          </div>
          {filterHook.hasCompetitors && (
            <div className="report-scope-field">
              <ToolbarSelect<MentionScope>
                prefix="Menções"
                icon={Building2}
                ariaLabel="De quem são as menções da amostra"
                value={filterHook.scope}
                onChange={next => filterHook.changeScope(next)}
                options={[
                  { value: "own", label: "A empresa" },
                  { value: "competitors", label: "Concorrentes" },
                  { value: "all", label: "A empresa e os concorrentes" }
                ]}
              />
            </div>
          )}
          <button
            type="button"
            className={`secondary-button report-more-filters${filterHook.filtersOpen ? " active" : ""}`}
            aria-pressed={filterHook.filtersOpen}
            onClick={() => {
              if (filterHook.filtersOpen) {
                filterHook.closePanel();
                return;
              }
              // Os dois painéis moram no mesmo canto da tela.
              setViewOpen(false);
              filterHook.openPanel();
            }}
          >
            <Filter size={16} />
            Mais filtros
            {filterHook.activeFilterCount > 0 && (
              <span className="filter-count">{filterHook.activeFilterCount}</span>
            )}
          </button>
        </div>
        {filterHook.activeChips.length > 0 && (
          <div className="active-filters-bar">
            <span className="active-filters-label">Filtros ativos:</span>
            {filterHook.activeChips.map(chip => (
              <button
                key={chip.key}
                type="button"
                className="active-filter-chip"
                onClick={chip.onRemove}
              >
                {chip.label}
                <X size={12} />
              </button>
            ))}
            <button
              type="button"
              className="text-button"
              onClick={() => {
                filterHook.commitFilters(emptyFilters());
                filterHook.setQuery("");
                filterHook.setSearchIn("all");
              }}
            >
              Limpar tudo
            </button>
          </div>
        )}
        {/* O aviso de meio-escopo vale nas TRÊS telas (decisão da dona,
            2026-08-27). Aqui ele importa até mais que em menções: o
            relatório é imprensa E rede misturados — o nome da tela
            induz ao erro de achar que é só rede —, então um filtro de
            tier que a pessoa montou pensando em imprensa deixa a rede
            inteira passar, e o total vem maior do que ela esperava. */}
        {meioEscopoNoRecorte().length > 0 && (
          <p className="form-hint">
            {meioEscopoNoRecorte().join(" ")} O filtro recorta a metade a
            que se aplica; a outra entra inteira.
          </p>
        )}
        {naoViajam().length > 0 && (
          <p className="form-hint">
            No filtro, mas fora da amostra: {naoViajam().join(", ")}. A
            amostra leva o resto do recorte.
          </p>
        )}
        <div className="report-filter-actions">
          {stale && (
            <span className="report-status-chip">
              Recorte de geração alterado — recalcule para atualizar a foto
            </span>
          )}
          <button
            type="button"
            className="primary-button"
            disabled={busy || running || !windowStart || !windowEnd}
            title={!windowStart || !windowEnd ? "Escolha o período" : undefined}
            onClick={() => void (canRecalculate ? recalculate() : generate())}
          >
            {running ? (
              <>
                <LoaderCircle size={15} className="spin"/> Gerando…
              </>
            ) : canRecalculate ? (
              "Recalcular"
            ) : (
              "Fechar amostra"
            )}
          </button>
        </div>
      </section>
      )}

      {!header && (
        <>
          <PendingDraftsBanner
            drafts={readOnly ? [] : drafts}
            activeDraftId={draft?.id ?? null}
            onOpen={draftId => void loadDetail(draftId)}
            onDiscard={draft => setDiscarding(draft)}
          />

          <SavedReportsList
            reports={savedReports}
            total={savedTotal}
            query={savedQuery}
            onQuery={setSavedQuery}
            brands={brands}
            activeReportId={saved?.id ?? null}
            reportHref={report => `/empresas/${companyId}/relatorios/cena?report=${report.id}`}
            onOpen={report => void openSavedReport(report.id)}
          />
        </>
      )}

      {running && (
        <ApiState kind="loading">
          Calculando o CENA de cada publicação do período. Isso roda em
          segundo plano — você pode sair desta tela e voltar depois.
        </ApiState>
      )}

      {error && <ApiState kind="error">{error}</ApiState>}
      <ExportFeedback exporter={exporter} suppressError={exportDialog} />
      {exportDialog && (saved || draft) && (
        <ExportFormatDialog
          token={token}
          columns
          canManagePresets={!readOnly}
          totalRows={total}
          busy={exporter.busy}
          error={exporter.error}
          onConfirm={choice => {
            const target = saved
              ? { kind: "report" as const, id: saved.id }
              : draft
                ? { kind: "draft" as const, id: draft.id }
                : null;
            if (target) void pedirPlanilha(target, choice);
          }}
          onCancel={() => setExportDialog(false)}
        />
      )}

      {run?.status === "ERRORED" && !error && (
        <ApiState kind="error" onRetry={() => void generate()}>
          {run.message ?? "A geração falhou."}
        </ApiState>
      )}

      {run?.status === "FINISHED_EMPTY" && !header && (
        <section className="card empty-state">
          <Sparkles size={22} aria-hidden="true"/>
          <p>{run.message ?? "Nenhuma publicação de rede social nesse período."}</p>
        </section>
      )}


      {header && (
        <>
          {viewActive > 0 && (
            <p className="form-hint report-view-notice">
              Os números abaixo seguem o filtro desta lista, não a foto
              inteira. Limpe o filtro para ver os valores congelados.
            </p>
          )}
          <section className="dashboard-kpi-grid report-kpi-grid">
            {/* O relatório é a FOTO; o CENA pegou carona nela. Empresa
                sem CENA continua tendo relatório — some a coluna, não a
                tela. */}
            <IfCena>
              <article className="dashboard-kpi">
                <CenaGauge value={aggregates?.avg_icena ?? null}/>
                <span className="dashboard-kpi-label">ICena médio</span>
                <strong className="dashboard-kpi-value">
                  {formatCena(aggregates?.avg_icena ?? null)}
                </strong>
                <span className="dashboard-kpi-context">
                  {formatWindowFromCriteria(
                    header.generation_criteria,
                    header.window_start,
                    header.window_end
                  )}
                  {brandNamesOf(header).length > 0 &&
                    ` · ${brandSummary(brandNamesOf(header), "")}`}
                  {/* A média é só das menções COM nota — sem esta linha,
                      "1.000 menções, média 6,2" com 300 neutras fora da
                      conta seria um número que não fecha (decisão da
                      dona, 2026-08-14). */}
                  {(aggregates?.unscored_count ?? 0) > 0 &&
                    ` · ${formatInteger(aggregates!.unscored_count)} sem nota (neutras)`}
                </span>
              </article>
            </IfCena>
            <article className="dashboard-kpi">
              <span className="dashboard-kpi-label">Publicações</span>
              <strong className="dashboard-kpi-value">
                {formatInteger(aggregates?.post_count ?? 0)}
              </strong>
              {/* "de rede social" estava errado desde sempre: a foto
                  carrega imprensa E rede misturadas (o `_press_items`
                  copia a metade de imprensa). Passava despercebido; com
                  a valoração — que é número SÓ de imprensa — ao lado, a
                  contradição fica na cara. */}
              <span className="dashboard-kpi-context">imprensa e rede, na foto</span>
            </article>
            <article className="dashboard-kpi">
              <span className="dashboard-kpi-label">Ultraperformance</span>
              <strong className="dashboard-kpi-value">
                {formatInteger(aggregates?.ultraperformance_count ?? 0)}
              </strong>
              <span className="dashboard-kpi-context">
                {(aggregates?.post_count ?? 0) > 0 &&
                  `${Math.round(((aggregates?.ultraperformance_count ?? 0) / (aggregates?.post_count ?? 1)) * 100)}% das publicações · `}
                acima do p95 do canal
              </span>
            </article>
            <article className="dashboard-kpi">
              <span className="dashboard-kpi-label">Valoração</span>
              <strong className="dashboard-kpi-value">
                {formatCurrencyCompact(aggregates?.total_ad_value ?? null)}
              </strong>
              <span className="dashboard-kpi-context">
                {aggregates?.total_ad_value == null
                  ? "nenhuma matéria valorada na foto"
                  : "soma das matérias de imprensa"}
              </span>
            </article>
          </section>

          <section className="card report-list">
            <div className="report-list-toolbar">
              <span className="report-list-count">
                <strong>{formatInteger(total)}</strong> publicações
                {viewActive > 0 && <em> · recorte de leitura</em>}
              </span>
              <div className="report-list-controls">
                {photoScope === "all" && (
                  <ToolbarSelect<MentionScope>
                    prefix="Menções"
                    icon={Building2}
                    ariaLabel="De quem são as menções mostradas"
                    align="right"
                    value={viewScope}
                    onChange={setViewScopeChoice}
                    options={[
                      { value: "own", label: "A empresa" },
                      { value: "competitors", label: "Concorrentes" },
                      { value: "all", label: "Todas" }
                    ]}
                  />
                )}
                {/* O filtro de LEITURA: só recorta o que se vê, nunca
                    vira foto (o de geração é o "Editar recorte"). */}
                <button
                  type="button"
                  className={`report-bar-button${viewOpen || viewActive > 0 ? " is-active" : ""}`}
                  aria-expanded={viewOpen}
                  onClick={() => {
                    const abrindo = !viewOpen;
                    setViewOpen(abrindo);
                    if (abrindo) {
                      // Os dois painéis moram no mesmo canto da tela.
                      filterHook.closePanel();
                      void carregarVocabulario();
                    }
                  }}
                >
                  <SlidersHorizontal size={15}/> Filtrar esta lista
                  {viewActive > 0 && <span className="filter-count">{viewActive}</span>}
                </button>
              </div>
            </div>
            {viewActive > 0 && (
              <div className="active-filters-bar">
                <span className="active-filters-label">Mostrando só:</span>
                {viewFrom && (
                  <button type="button" className="active-filter-chip" onClick={() => setViewFrom("")}>
                    de {viewFrom}
                    <X size={12}/>
                  </button>
                )}
                {viewTo && (
                  <button type="button" className="active-filter-chip" onClick={() => setViewTo("")}>
                    até {viewTo}
                    <X size={12}/>
                  </button>
                )}
                {viewChips().map(chip => (
                  <button key={chip.key} type="button" className="active-filter-chip" onClick={chip.remove}>
                    {chip.label}
                    <X size={12}/>
                  </button>
                ))}
                <button type="button" className="text-button" onClick={limparView}>
                  Limpar filtro
                </button>
              </div>
            )}
            <div role="table" className="social-report-table">
              <SocialReportListHeader
                order={order}
                onChangeOrder={next => {
                  setOrder(next);
                  setPage(1);
                  void reload(1, next);
                }}
              />
              {loading ? (
                <ApiState kind="loading">Carregando…</ApiState>
              ) : items.length === 0 && viewActive > 0 ? (
                // Vazio POR FILTRO, e a distinção importa: sem esta
                // mensagem a tela lê como "o relatório não tem nada",
                // e o relatório tem — é o recorte que não casou.
                <div className="empty-state">
                  <SlidersHorizontal size={30} aria-hidden="true"/>
                  <strong>Nenhuma publicação passa por este filtro</strong>
                  <span>
                    A foto continua inteira — remova um filtro para
                    voltar a vê-la.
                  </span>
                </div>
              ) : (
                items.map(item => (
                  <SocialReportRow
                    key={item.mention_id}
                    item={item}
                    onOpen={mentionId => void openItem(mentionId)}
                  />
                ))
              )}
            </div>
            <Pager
              page={page}
              size={PAGE_SIZE}
              count={{ total }}
              onPage={next => {
                setPage(next);
                void reload(next);
              }}
            />
          </section>

        </>
      )}

      {/* O painel "Mais filtros" do recorte de GERAÇÃO — o mesmo da tela
          de Menções (`MentionFilterPanel`), sem os controles que o
          relatório não leva. Irmão de topo, como o de leitura, pra regra
          de margem do CSS enxergá-lo. */}
      {showForm && filterHook.filtersOpen && (
        <MentionFilterPanel
          hook={filterHook}
          mode="report"
          showsCena={showsCena}
          eyebrow="AMOSTRA"
          className="report-generation-drawer"
          uploadOptions={uploadOptions}
          uploadFilter={uploadIds}
          onChangeUploadFilter={setUploadIds}
        />
      )}

      {header && viewOpen && (
        <aside className="drawer filters-panel report-view-drawer" aria-label="Filtrar a lista da amostra">
          <div className="drawer-head">
            <div>
              <span>AMOSTRA</span>
              <strong>Filtrar esta lista</strong>
            </div>
            <button className="icon-button" aria-label="Fechar filtros" onClick={() => setViewOpen(false)}>
              <X size={19}/>
            </button>
          </div>
          <div className="drawer-body">
            <p className="form-hint report-view-drawer-hint">
              Recorta o que você vê. A foto não muda, e nada aqui precisa
              de recálculo.
              {photoScope === "all" &&
                " Esta amostra tem concorrentes: os números contam só o que está em \"Menções\"."}
            </p>
            <div className="filter-facets report-view-facets">
                  {viewCatalogBusy && (
                    <ApiState kind="loading">
                      Carregando as opções desta foto…
                    </ApiState>
                  )}
                  <MultiSelectFilter
                    label="Sentimento"
                    options={polarityOptions().map(o => ({
                      value: o.name,
                      label: o.label
                    }))}
                    selected={viewFacets.sentiments.values}
                    exclude={viewFacets.sentiments.exclude}
                    onExcludeChange={v => mudarView("sentiments", f => ({ ...f, exclude: v }))}
                    onToggle={v => toggleView("sentiments", v)}
                    onClear={() => mudarView("sentiments", f => ({ ...f, values: new Set() }))}
                  />
                  <MultiSelectFilter
                    label="Fonte"
                    options={sourceOptions}
                    selected={viewFacets.sources.values}
                    exclude={viewFacets.sources.exclude}
                    onExcludeChange={v => mudarView("sources", f => ({ ...f, exclude: v }))}
                    onToggle={v => toggleView("sources", v)}
                    onClear={() => mudarView("sources", f => ({ ...f, values: new Set() }))}
                  />
                  <div className="report-view-window">
                    <label>
                      Publicado de
                      <DateField
                        ariaLabel="Filtrar por data inicial de publicação"
                        value={viewFrom}
                        onValue={setViewFrom}
                      />
                    </label>
                    <label>
                      até
                      <DateField
                        ariaLabel="Filtrar por data final de publicação"
                        value={viewTo}
                        align="right"
                        onValue={setViewTo}
                      />
                    </label>
                    {/* Relatório gerado antes do congelamento da data
                        não tem `published_on` por publicação, e o filtro
                        derruba a lista inteira. O dashboard cala nesse
                        caso; aqui não dá pra calar, porque na lista o
                        sumiço é linha a linha e lê como bug. */}
                    <p className="form-hint">
                      Filtra pelo dia congelado na foto. Amostras
                      antigas podem não ter essa data em cada publicação
                      — nelas, a lista vem vazia.
                    </p>
                  </div>
                  {VIEW_CONTROLS.map(({ key, label, hint }) => (
                    <MultiSelectFilter
                      key={key}
                      label={label}
                      hint={hint}
                      options={optionsForView(key)}
                      selected={viewFacets[key].values}
                      exclude={viewFacets[key].exclude}
                      onExcludeChange={v => mudarView(key, f => ({ ...f, exclude: v }))}
                      onToggle={v => toggleView(key, v)}
                      onClear={() => mudarView(key, f => ({ ...f, values: new Set() }))}
                    />
                  ))}
                </div>
          </div>
          <div className="drawer-foot">
            <button type="button" className="text-button" onClick={limparView} disabled={viewActive === 0}>
              Limpar filtro
            </button>
            <button type="button" className="primary-button" onClick={() => setViewOpen(false)}>
              Ver {formatInteger(total)} publicações
            </button>
          </div>
        </aside>
      )}

      {naming && draft && (
        <ConfirmDialog
          title="Salvar amostra"
          description="O nome aparece na lista de amostras e no dashboard."
          confirmLabel="Salvar"
          loading={busy}
          error={nameError}
          requireField={{
            label: "Nome da amostra",
            type: "text",
            placeholder: "Ex.: Balanço de agosto",
            // Sugestão pronta pra confirmar: quem não quer nomear
            // aperta Enter, quem quer, renomeia.
            defaultValue: `${brandSummary(
              brandNamesOf(draft),
              "Todas as marcas"
            )} — ${formatWindowFromCriteria(draft.generation_criteria, draft.window_start, draft.window_end)}`,
            maxLength: 120
          }}
          onConfirm={name => void promote(name)}
          onCancel={() => {
            setNaming(false);
            setNameError(null);
          }}
        />
      )}

      {discarding && (
        <ConfirmDialog
          title="Descartar rascunho"
          description="O cálculo será perdido e a amostra precisará ser gerada de novo. Amostras já salvas não são afetadas."
          confirmLabel="Descartar"
          danger
          loading={busy}
          onConfirm={() => void confirmDiscard()}
          onCancel={() => setDiscarding(null)}
        />
      )}
    </div>
  );
}
